<template>
  <div>
    <v-tabs v-model="selectedTab">
      <v-tab>Settings</v-tab>
      <v-tab v-for="(item, index) in items" :key="'item-' + index">Item {{ index + 1 }}</v-tab>
      <v-spacer></v-spacer>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon tile @click="addItem">
            <v-icon>fal fa-plus</v-icon>
          </v-btn>
        </template>
        <span>Add Item to Carousel</span>
      </v-tooltip>
    </v-tabs>
    <v-tabs-items v-model="selectedTab">
      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-text-field v-model="name" label="Name of block (for administrative purposes; optional)"></v-text-field>
            <v-checkbox v-model="cycle" label="Automatically scroll through the items every 6 seconds"></v-checkbox>
            <v-checkbox v-if="!cycle" v-model="mustComplete" label="The user must scroll through all of the content before they can continue"></v-checkbox>
            <v-checkbox v-model="showArrows" label="Include arrows for manual navigation"></v-checkbox>
            <v-checkbox v-model="delimiters" label="Show the delimiter icons at the bottom (allows student to click on any to view)"></v-checkbox>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item v-for="(item, index) in items" :key="'item-content-' + index">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col :cols="4">
                <v-checkbox v-model="item.backgroundImage" label="Use Background Image" hide-details></v-checkbox>
                <v-switch v-if="item.backgroundImage" v-model="item.contain" :label="item.contain ? 'Constrain image to fit in the box' : 'Overflow the image to fill the box'" hide-details></v-switch>
              </v-col>
              <v-col :cols="4" v-if="item.backgroundImage">
                <v-file-input label="Select Image" accept="image/*" hide-details outlined @change="(val) => loadImage(val, index)"></v-file-input>
              </v-col>
              <v-col :cols="4">
                <v-select v-model="item.backgroundColor" :items="colors" :label="item.backgroundImage ? 'Text Background Color' : 'Background Color'" hide-details outlined></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col v-if="item.backgroundImage" :cols="3">
                <v-text-field v-model="item.opacity" label="Background Opacity" type="number" min="0" max="100" step="10" hint="100 for solid, 0 for transparent" hide-details outlined></v-text-field>
              </v-col>
              <v-col :cols="item.backgroundImage ? 3 : 4">
                <v-select v-model="item.textColor" :items="colors" label="Text Color" hide-details outlined></v-select>
              </v-col>
              <v-col :cols="item.backgroundImage ? 3 : 4">
                <v-select v-model="item.align" :items="alignOptions" label="Text Vertical Alignment" hide-details outlined></v-select>
              </v-col>
              <v-col :cols="item.backgroundImage ? 3 : 4">
                <v-select v-model="item.justify" :items="justifyOptions" label="Text Horizontal Alignment" hide-details outlined></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col :cols="6">
                <h4>Text within item (optional)</h4>
                <html-editor v-model="item.text"></html-editor>
                <v-btn text color="error" @click="removeItem(index)">Remove Carousel Item</v-btn>
              </v-col>
              <v-col :cols="6">
                <h4>Preview</h4>
                <v-carousel :hide-delimiters="true" :show-arrows="false" height="250px">
                  <v-carousel-item :src="item.backgroundImage ? item.image : ''" :contain="item.contain">
                    <v-sheet :color="item.backgroundImage ? 'transparent' : item.backgroundColor" height="100%">
                      <v-row class="fill-height" :align="item.align" :justify="item.justify">
                        <div v-html="item.text" :style="'color:' + colorOpacity(item.textColor, item.opacity) + ';' + (item.backgroundImage ? 'background-color:' + colorOpacity(item.backgroundColor, item.opacity) + ';' : '') + 'text-align:' + (item.justify === 'start' ? 'left' : (item.justify === 'end' ? 'right' : 'center'))" class="carousel-overlay-text" style="margin:0 12px"></div>
                      </v-row>
                    </v-sheet>
                  </v-carousel-item>
                </v-carousel>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>
<style scoped>
div.image-card-overlay {
  padding: .5em;
  text-align: center;
}
div.image-card-overlay.dark {
  background-color: rgba(0,0,0,.8);
  color: white;
}
div.image-card-overlay.light {
  background-color: rgba(255,255,255,.8);
  color: black;
}
div.carousel-overlay-text {
  padding: .7em;
}
div.carousel-overlay-text :last-child {
  margin-bottom: 0
}
</style>
<script>
import { ref, watch } from '@vue/composition-api'
export default {
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  components: {
    HtmlEditor: () => import('@/components/forms/HTMLEditor')
  },
  setup (props, { root, emit }) {
    const selectedTab = ref(0)
    const name = ref(props.value.name || '')
    const cycle = ref('cycle' in props.value ? props.value.cycle : true)
    const mustComplete = ref('mustComplete' in props.value ? props.value.mustComplete : false)
    const showArrows = ref('showArrows' in props.value ? props.value.showArrows : true)
    const delimiters = ref('delimiters' in props.value ? props.value.delimiters : true)
    const items = ref([])

    watch(() => props.value, () => {
      name.value = props.value.name || ''
      cycle.value = 'cycle' in props.value ? props.value.cycle : true
      mustComplete.value = 'mustComplete' in props.value ? props.value.mustComplete : false
      showArrows.value = 'showArrows' in props.value ? props.value.showArrows : true
      delimiters.value = 'delimiters' in props.value ? props.value.delimiters : true
      mustComplete.value = 'mustComplete' in props.value ? props.value.mustComplete : false
      items.value = props.value.items || []
    })

    watch(name, () => name.value !== props.value.name ? emit('update', { name: name.value }) : '')
    watch(cycle, () => cycle.value !== props.value.cycle ? emit('update', { cycle: cycle.value }) : '')
    watch(mustComplete, () => mustComplete.value !== props.value.mustComplete ? emit('update', { mustComplete: mustComplete.value }) : '')
    watch(showArrows, () => showArrows.value !== props.value.showArrows ? emit('update', { showArrows: showArrows.value }) : '')
    watch(delimiters, () => delimiters.value !== props.value.delimiters ? emit('update', { delimiters: delimiters.value }) : '')
    watch(items, () => items.value !== props.value.items ? emit('update', { items: items.value }) : '')

    function addItem () {
      items.value.push({
        text: '<h1></h1>',
        backgroundImage: false,
        contain: false,
        image: '',
        backgroundColor: 'white',
        opacity: 60,
        textColor: 'black',
        align: 'center',
        justify: 'center'
      })
      setTimeout(() => { selectedTab.value = items.value.length }, 100)
    }
    function removeItem (index) {
      if (confirm('Are you sure you want to remove this item from the carousel?')) {
        items.value.splice(index, 1)
      }
    }

    function loadImage (val, index) {
      if (val == null) {
        let temp = items.value[index]
        temp.image = null
        items.value.splice(index, 1, temp)
        return
      }
      const reader = new FileReader()
      reader.readAsDataURL(val)
      reader.onload = () => {
        let temp = items.value[index]
        temp.image = reader.result
        items.value.splice(index, 1, temp)
      }
      reader.onerror = (error) => { root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'Error loading file: ' + error }) }
    }

    const colors = ref(['white', 'black', 'blue', 'green', 'red'])
    const alignOptions = ref([{ text: 'top', value: 'start' }, { text: 'center', value: 'center' }, { text: 'bottom', value: 'end' }])
    const justifyOptions = ref([{ text: 'left', value: 'start' }, { text: 'center', value: 'center' }, { text: 'right', value: 'end' }])

    function colorOpacity (color, opacity) {
      switch (color) {
        case 'white':
          return 'rgba(255, 255, 255, ' + (opacity / 100) + ')'
        case 'black':
          return 'rgba(0, 0, 0, ' + (opacity / 100) + ')'
        case 'blue':
          return 'rgba(0, 0, 255, ' + (opacity / 100) + ')'
        case 'green':
          return 'rgba(0, 255, 0, ' + (opacity / 100) + ')'
        case 'red':
          return 'rgba(255, 0, 0, ' + (opacity / 100) + ')'
      }
      return 'transparent'
    }

    function validate () {
      if (!cycle.value && !showArrows.value && !delimiters.value && mustComplete.value) {
        alert('There is no way to progress the carousel through the slides. Please either enable manual control via the arrows or delimiters, or set the carousel to cycle automatically.')
        return false
      }
      return true
    }

    return {
      selectedTab,
      cycle,
      mustComplete,
      showArrows,
      delimiters,
      name,
      items,
      addItem,
      removeItem,
      loadImage,
      colors,
      alignOptions,
      justifyOptions,
      colorOpacity,
      validate
    }
  }
}
</script>
